import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { LinkedList } from 'vscode/vscode/vs/base/common/linkedList';
import { ResourceMap, ResourceSet } from 'vscode/vscode/vs/base/common/map';
import { isCodeEditor, isDiffEditor } from 'vscode/vscode/vs/editor/browser/editorBrowser';
import { ResourceTextEdit, ResourceFileEdit } from 'vscode/vscode/vs/editor/browser/services/bulkEditService';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { IDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { Progress } from 'vscode/vscode/vs/platform/progress/common/progress';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { UndoRedoGroup } from 'vscode/vscode/vs/platform/undoRedo/common/undoRedo';
import { ResourceNotebookCellEdit, BulkCellEdits } from 'vscode/vscode/vs/workbench/contrib/bulkEdit/browser/bulkCellEdits';
import { BulkFileEdits } from './bulkFileEdits.js';
import { BulkTextEdits } from './bulkTextEdits.js';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { ILifecycleService } from 'vscode/vscode/vs/workbench/services/lifecycle/common/lifecycle.service';
import { IWorkingCopyService } from 'vscode/vscode/vs/workbench/services/workingCopy/common/workingCopyService.service';

const _moduleId = "vs/workbench/contrib/bulkEdit/browser/bulkEditService";
function liftEdits(edits) {
    return (
         (edits.map(edit => {
            if (ResourceTextEdit.is(edit)) {
                return ResourceTextEdit.lift(edit);
            }
            if (ResourceFileEdit.is(edit)) {
                return ResourceFileEdit.lift(edit);
            }
            if (ResourceNotebookCellEdit.is(edit)) {
                return ResourceNotebookCellEdit.lift(edit);
            }
            throw ( (new Error('Unsupported edit')));
        }))
    );
}
let BulkEdit = class BulkEdit {
    constructor(_label, _code, _editor, _progress, _token, _edits, _undoRedoGroup, _undoRedoSource, _confirmBeforeUndo, _instaService, _logService) {
        this._label = _label;
        this._code = _code;
        this._editor = _editor;
        this._progress = _progress;
        this._token = _token;
        this._edits = _edits;
        this._undoRedoGroup = _undoRedoGroup;
        this._undoRedoSource = _undoRedoSource;
        this._confirmBeforeUndo = _confirmBeforeUndo;
        this._instaService = _instaService;
        this._logService = _logService;
    }
    ariaMessage() {
        const otherResources = ( (new ResourceMap()));
        const textEditResources = ( (new ResourceMap()));
        let textEditCount = 0;
        for (const edit of this._edits) {
            if (edit instanceof ResourceTextEdit) {
                textEditCount += 1;
                textEditResources.set(edit.resource, true);
            }
            else if (edit instanceof ResourceFileEdit) {
                otherResources.set(edit.oldResource ?? edit.newResource, true);
            }
        }
        if (this._edits.length === 0) {
            return ( localizeWithPath(_moduleId, 0, "Made no edits"));
        }
        else if (otherResources.size === 0) {
            if (textEditCount > 1 && textEditResources.size > 1) {
                return ( localizeWithPath(
                    _moduleId,
                    1,
                    "Made {0} text edits in {1} files",
                    textEditCount,
                    textEditResources.size
                ));
            }
            else {
                return ( localizeWithPath(_moduleId, 2, "Made {0} text edits in one file", textEditCount));
            }
        }
        else {
            return ( localizeWithPath(
                _moduleId,
                3,
                "Made {0} text edits in {1} files, also created or deleted {2} files",
                textEditCount,
                textEditResources.size,
                otherResources.size
            ));
        }
    }
    async perform() {
        if (this._edits.length === 0) {
            return [];
        }
        const ranges = [1];
        for (let i = 1; i < this._edits.length; i++) {
            if (Object.getPrototypeOf(this._edits[i - 1]) === Object.getPrototypeOf(this._edits[i])) {
                ranges[ranges.length - 1]++;
            }
            else {
                ranges.push(1);
            }
        }
        const increment = this._edits.length > 1 ? 0 : undefined;
        this._progress.report({ increment, total: 100 });
        const progress = { report: _ => this._progress.report({ increment: 100 / this._edits.length }) };
        const resources = [];
        let index = 0;
        for (const range of ranges) {
            if (this._token.isCancellationRequested) {
                break;
            }
            const group = this._edits.slice(index, index + range);
            if (group[0] instanceof ResourceFileEdit) {
                resources.push(await this._performFileEdits(group, this._undoRedoGroup, this._undoRedoSource, this._confirmBeforeUndo, progress));
            }
            else if (group[0] instanceof ResourceTextEdit) {
                resources.push(await this._performTextEdits(group, this._undoRedoGroup, this._undoRedoSource, progress));
            }
            else if (group[0] instanceof ResourceNotebookCellEdit) {
                resources.push(await this._performCellEdits(group, this._undoRedoGroup, this._undoRedoSource, progress));
            }
            else {
                console.log('UNKNOWN EDIT');
            }
            index = index + range;
        }
        return resources.flat();
    }
    async _performFileEdits(edits, undoRedoGroup, undoRedoSource, confirmBeforeUndo, progress) {
        this._logService.debug('_performFileEdits', JSON.stringify(edits));
        const model = this._instaService.createInstance(BulkFileEdits, this._label || ( localizeWithPath(_moduleId, 4, "Workspace Edit")), this._code || 'undoredo.workspaceEdit', undoRedoGroup, undoRedoSource, confirmBeforeUndo, progress, this._token, edits);
        return await model.apply();
    }
    async _performTextEdits(edits, undoRedoGroup, undoRedoSource, progress) {
        this._logService.debug('_performTextEdits', JSON.stringify(edits));
        const model = this._instaService.createInstance(BulkTextEdits, this._label || ( localizeWithPath(_moduleId, 4, "Workspace Edit")), this._code || 'undoredo.workspaceEdit', this._editor, undoRedoGroup, undoRedoSource, progress, this._token, edits);
        return await model.apply();
    }
    async _performCellEdits(edits, undoRedoGroup, undoRedoSource, progress) {
        this._logService.debug('_performCellEdits', JSON.stringify(edits));
        const model = this._instaService.createInstance(BulkCellEdits, undoRedoGroup, undoRedoSource, progress, this._token, edits);
        return await model.apply();
    }
};
BulkEdit = ( (__decorate([
    ( (__param(9, IInstantiationService))),
    ( (__param(10, ILogService)))
], BulkEdit)));
let BulkEditService = class BulkEditService {
    constructor(_instaService, _logService, _editorService, _lifecycleService, _dialogService, _workingCopyService, _configService) {
        this._instaService = _instaService;
        this._logService = _logService;
        this._editorService = _editorService;
        this._lifecycleService = _lifecycleService;
        this._dialogService = _dialogService;
        this._workingCopyService = _workingCopyService;
        this._configService = _configService;
        this._activeUndoRedoGroups = ( (new LinkedList()));
    }
    setPreviewHandler(handler) {
        this._previewHandler = handler;
        return toDisposable(() => {
            if (this._previewHandler === handler) {
                this._previewHandler = undefined;
            }
        });
    }
    hasPreviewHandler() {
        return Boolean(this._previewHandler);
    }
    async apply(editsIn, options) {
        let edits = liftEdits(Array.isArray(editsIn) ? editsIn : editsIn.edits);
        if (edits.length === 0) {
            return { ariaSummary: ( localizeWithPath(_moduleId, 5, "Made no edits")), isApplied: false };
        }
        if (this._previewHandler && (options?.showPreview || ( (edits.some(value => value.metadata?.needsConfirmation))))) {
            edits = await this._previewHandler(edits, options);
        }
        let codeEditor = options?.editor;
        if (!codeEditor) {
            const candidate = this._editorService.activeTextEditorControl;
            if (isCodeEditor(candidate)) {
                codeEditor = candidate;
            }
            else if (isDiffEditor(candidate)) {
                codeEditor = candidate.getModifiedEditor();
            }
        }
        if (codeEditor && codeEditor.getOption(91 )) {
            codeEditor = undefined;
        }
        let undoRedoGroup;
        let undoRedoGroupRemove = () => { };
        if (typeof options?.undoRedoGroupId === 'number') {
            for (const candidate of this._activeUndoRedoGroups) {
                if (candidate.id === options.undoRedoGroupId) {
                    undoRedoGroup = candidate;
                    break;
                }
            }
        }
        if (!undoRedoGroup) {
            undoRedoGroup = ( (new UndoRedoGroup()));
            undoRedoGroupRemove = this._activeUndoRedoGroups.push(undoRedoGroup);
        }
        const label = options?.quotableLabel || options?.label;
        const bulkEdit = this._instaService.createInstance(BulkEdit, label, options?.code, codeEditor, options?.progress ?? Progress.None, options?.token ?? CancellationToken.None, edits, undoRedoGroup, options?.undoRedoSource, !!options?.confirmBeforeUndo);
        let listener;
        try {
            listener = this._lifecycleService.onBeforeShutdown(e => e.veto(this._shouldVeto(label, e.reason), 'veto.blukEditService'));
            const resources = await bulkEdit.perform();
            if (options?.respectAutoSaveConfig && this._configService.getValue(autoSaveSetting) === true && resources.length > 1) {
                await this._saveAll(resources);
            }
            return { ariaSummary: bulkEdit.ariaMessage(), isApplied: edits.length > 0 };
        }
        catch (err) {
            this._logService.error(err);
            throw err;
        }
        finally {
            listener?.dispose();
            undoRedoGroupRemove();
        }
    }
    async _saveAll(resources) {
        const set = ( (new ResourceSet(resources)));
        const saves = ( (this._workingCopyService.dirtyWorkingCopies.map(async (copy) => {
            if (( (set.has(copy.resource)))) {
                await copy.save();
            }
        })));
        const result = await Promise.allSettled(saves);
        for (const item of result) {
            if (item.status === 'rejected') {
                this._logService.warn(item.reason);
            }
        }
    }
    async _shouldVeto(label, reason) {
        let message;
        let primaryButton;
        switch (reason) {
            case 1 :
                message = ( localizeWithPath(_moduleId, 6, "Are you sure you want to close the window?"));
                primaryButton = ( localizeWithPath(_moduleId, 7, "&&Close Window"));
                break;
            case 4 :
                message = ( localizeWithPath(_moduleId, 8, "Are you sure you want to change the workspace?"));
                primaryButton = ( localizeWithPath(_moduleId, 9, "Change &&Workspace"));
                break;
            case 3 :
                message = ( localizeWithPath(_moduleId, 10, "Are you sure you want to reload the window?"));
                primaryButton = ( localizeWithPath(_moduleId, 11, "&&Reload Window"));
                break;
            default:
                message = ( localizeWithPath(_moduleId, 12, "Are you sure you want to quit?"));
                primaryButton = ( localizeWithPath(_moduleId, 13, "&&Quit"));
                break;
        }
        const result = await this._dialogService.confirm({
            message,
            detail: ( localizeWithPath(_moduleId, 14, "'{0}' is in progress.", label || ( localizeWithPath(_moduleId, 15, "File operation")))),
            primaryButton
        });
        return !result.confirmed;
    }
};
BulkEditService = ( (__decorate([
    ( (__param(0, IInstantiationService))),
    ( (__param(1, ILogService))),
    ( (__param(2, IEditorService))),
    ( (__param(3, ILifecycleService))),
    ( (__param(4, IDialogService))),
    ( (__param(5, IWorkingCopyService))),
    ( (__param(6, IConfigurationService)))
], BulkEditService)));
const autoSaveSetting = 'files.refactoring.autoSave';
( (Registry.as(Extensions.Configuration))).registerConfiguration({
    id: 'files',
    properties: {
        [autoSaveSetting]: {
            description: ( localizeWithPath(
                _moduleId,
                16,
                "Controls if files that were part of a refactoring are saved automatically"
            )),
            default: true,
            type: 'boolean'
        }
    }
});

export { BulkEditService };
